import { useState, useEffect } from 'react';
import { useRequireAdminAuth } from '../../hooks/useAdminAuth';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { getAIUsageStats, type AIUsageStats } from '../../lib/admin-api';
import {
  Loader2,
  AlertCircle,
  Zap,
  DollarSign,
  Clock,
  AlertTriangle,
  RefreshCw,
  Activity,
} from 'lucide-react';

export default function AIUsageView() {
  const { isLoading: authLoading, isAuthenticated } = useRequireAdminAuth();

  const [stats, setStats] = useState<AIUsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const fetchStats = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAIUsageStats(dateFrom || undefined, dateTo || undefined);
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI usage stats');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchStats();
  }, [isAuthenticated]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <AdminLayout title="AI Usage & Cost Dashboard">
      {/* Date Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div>
          <Label htmlFor="dateFrom" className="text-sm text-gray-600">From</Label>
          <Input
            id="dateFrom"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-40"
          />
        </div>
        <div>
          <Label htmlFor="dateTo" className="text-sm text-gray-600">To</Label>
          <Input
            id="dateTo"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-40"
          />
        </div>
        <Button onClick={fetchStats} disabled={isLoading} size="sm">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading && !stats ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : stats ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<Zap className="h-5 w-5 text-blue-500" />}
              label="Total AI Calls"
              value={stats.summary.totalCalls.toLocaleString()}
            />
            <StatCard
              icon={<Activity className="h-5 w-5 text-purple-500" />}
              label="Total Tokens"
              value={stats.summary.totalTokens.toLocaleString()}
              sub={`${stats.summary.totalPromptTokens.toLocaleString()} in / ${stats.summary.totalCompletionTokens.toLocaleString()} out${
                stats.summary.totalCacheReadTokens > 0 ? ` · ${stats.summary.totalCacheReadTokens.toLocaleString()} cached` : ''
              }`}
            />
            <StatCard
              icon={<DollarSign className="h-5 w-5 text-green-500" />}
              label="Estimated Cost"
              value={`$${stats.summary.estimatedCost.toFixed(2)}`}
              sub="Claude Sonnet 4.5 pricing"
            />
            <StatCard
              icon={<Clock className="h-5 w-5 text-orange-500" />}
              label="Avg Response Time"
              value={`${(stats.summary.avgResponseTimeMs / 1000).toFixed(1)}s`}
              sub={`Parse fail: ${(stats.summary.parseFailureRate * 100).toFixed(1)}% | Errors: ${(stats.summary.errorRate * 100).toFixed(1)}%`}
            />
          </div>

          {/* By Endpoint */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Usage by Endpoint</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.byEndpoint.length === 0 ? (
                <p className="text-gray-500 text-sm">No data yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-2 pr-4">Endpoint</th>
                        <th className="pb-2 pr-4 text-right">Calls</th>
                        <th className="pb-2 pr-4 text-right">Total Tokens</th>
                        <th className="pb-2 pr-4 text-right">Avg Tokens</th>
                        <th className="pb-2 pr-4 text-right">Avg Time</th>
                        <th className="pb-2 pr-4 text-right">Parse Fail</th>
                        <th className="pb-2 text-right">Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.byEndpoint.map((ep) => (
                        <tr key={ep.endpoint} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-mono text-xs">{ep.endpoint}</td>
                          <td className="py-2 pr-4 text-right">{ep.calls.toLocaleString()}</td>
                          <td className="py-2 pr-4 text-right">{ep.totalTokens.toLocaleString()}</td>
                          <td className="py-2 pr-4 text-right">{ep.avgTokens.toLocaleString()}</td>
                          <td className="py-2 pr-4 text-right">{(ep.avgResponseTimeMs / 1000).toFixed(1)}s</td>
                          <td className="py-2 pr-4 text-right">
                            <span className={ep.parseFailureRate > 0.05 ? 'text-red-600 font-medium' : ''}>
                              {(ep.parseFailureRate * 100).toFixed(1)}%
                            </span>
                          </td>
                          <td className="py-2 text-right">
                            <span className={ep.errorRate > 0.05 ? 'text-red-600 font-medium' : ''}>
                              {(ep.errorRate * 100).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Daily Trend + By Model side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Daily Trend (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                {stats.daily.length === 0 ? (
                  <p className="text-gray-500 text-sm">No data yet</p>
                ) : (
                  <div className="space-y-1 max-h-80 overflow-y-auto">
                    {stats.daily.map((day) => {
                      const maxCalls = Math.max(...stats.daily.map(d => d.calls), 1);
                      const barWidth = Math.max((day.calls / maxCalls) * 100, 2);
                      return (
                        <div key={day.date} className="flex items-center gap-2 text-xs">
                          <span className="w-20 text-gray-500 shrink-0">{day.date.slice(5)}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${day.errors > 0 ? 'bg-orange-400' : 'bg-blue-400'}`}
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                          <span className="w-12 text-right text-gray-600 shrink-0">{day.calls}</span>
                          <span className="w-16 text-right text-gray-400 shrink-0">{day.totalTokens.toLocaleString()}t</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* By Model */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Usage by Model</CardTitle>
              </CardHeader>
              <CardContent>
                {stats.byModel.length === 0 ? (
                  <p className="text-gray-500 text-sm">No data yet</p>
                ) : (
                  <div className="space-y-3">
                    {stats.byModel.map((model) => (
                      <div key={model.model} className="flex items-center justify-between border-b pb-2 last:border-0">
                        <div>
                          <p className="font-mono text-xs">{model.model}</p>
                          <p className="text-gray-400 text-xs">{model.calls} calls</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{model.totalTokens.toLocaleString()} tokens</p>
                          <p className="text-gray-400 text-xs">avg {model.avgTokens}/call</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Errors */}
          {stats.recentErrors.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Recent Errors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {stats.recentErrors.map((err, i) => (
                    <div key={i} className="flex items-start gap-3 text-xs border-b pb-2 last:border-0">
                      <span className="font-mono text-gray-500 shrink-0 w-36">
                        {new Date(err.createdAt).toLocaleString()}
                      </span>
                      <span className="font-mono text-orange-600 shrink-0 w-40">{err.endpoint}</span>
                      <span className="text-red-600 break-all">{err.error}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : null}
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          {icon}
          <div>
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
